import AppCover from "../components/ds/AppCover";
import Badge from "../components/ds/Badge";
import Button from "../components/ds/Button";
import EmptyState from "../components/ds/EmptyState";
import Icon from "../components/ds/Icon";
import Menu, { MenuItem } from "../components/ds/Menu";
import ProgressBar from "../components/ds/ProgressBar";
import { useState, useEffect, useCallback, useRef } from "react";
import {
  FiPlus,
  FiTrash2,
  FiLock,
  FiCheckCircle,
  FiPaperclip,
  FiDownload,
  FiChevronLeft,
  FiChevronUp,
  FiChevronDown,
  FiBookOpen,
  FiUpload,
  FiLoader,
  FiSettings,
  FiFileText,
  FiVideo,
  FiImage,
  FiGlobe,
  FiDollarSign,
  FiChevronRight,
} from "react-icons/fi";
import { useTranslation } from "../context/TranslationContext";
import ConfirmModal from "../components/ConfirmModal";
import CustomVideoPlayer from "../components/CustomVideoPlayer";
import MintSpaceAppWindow from "../components/MintSpaceAppWindow";
import {
  listCourses,
  createCourse,
  getCourseDetail,
  updateCourse,
  deleteCourse,
  reorderCourse,
  uploadCourseCover,
  getCourseCoverUrl,
  createChapter,
  updateChapter,
  deleteChapter,
  createLesson,
  getLessonDetail,
  updateLesson,
  deleteLesson,
  uploadLessonVideo,
  uploadLessonPdf,
  fetchLessonVideoBlob,
  getLessonVideoStreamUrl,
  getLessonPdfUrl,
  saveLessonProgress,
  uploadLessonAttachment,
  deleteLessonAttachment,
  getLessonAttachmentDownloadUrl,
} from "../api/mintSpaceCourses";

const formatDuration = (seconds) => {
  if (!seconds || !Number.isFinite(seconds)) return "";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const formatFileSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const LARGE_VIDEO_BYTES = 80 * 1024 * 1024;

const MintSpaceCoursesPage = ({
  isDark,
  setToast,
  mintSpace,
  isAdmin = false,
  appInstanceId,
  onCreateProduct,
  onOpenProperties,
}) => {
  const { t } = useTranslation();
  const mintSpaceId = mintSpace?._id;

  const [view, setView] = useState("list"); // list | workspace
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  const [activeCourseId, setActiveCourseId] = useState(null);
  const [courseDetail, setCourseDetail] = useState(null);
  const [loadingCourse, setLoadingCourse] = useState(false);
  const [selectedLessonId, setSelectedLessonId] = useState(null);
  const [lessonDetail, setLessonDetail] = useState(null);
  const [loadingLesson, setLoadingLesson] = useState(false);

  const [showCreateCourse, setShowCreateCourse] = useState(false);
  const [newCourseTitle, setNewCourseTitle] = useState("");
  const [newCourseDesc, setNewCourseDesc] = useState("");
  const [creatingCourse, setCreatingCourse] = useState(false);

  const [showSettings, setShowSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    title: "",
    description: "",
    requireCompletingLessonsInOrder: false,
    visibility: "public",
  });
  const [savingSettings, setSavingSettings] = useState(false);

  const [lessonForm, setLessonForm] = useState({
    title: "",
    content: "",
    daysFromCourseStartUntilUnlock: 0,
  });
  const [savingLesson, setSavingLesson] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [addLessonChapterId, setAddLessonChapterId] = useState(null);
  const lessonMenuAnchor = useRef(null);
  const closeLessonMenu = useCallback(() => setAddLessonChapterId(null), []);

  const progressSaveRef = useRef(null);
  const lastProgressRef = useRef(0);
  const videoInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  const attachmentInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const [videoSrc, setVideoSrc] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const videoObjectUrlRef = useRef(null);

  const baseText = "text-label-primary";
  const mutedText = "text-label-secondary";
  const cardBase = "border-separator-default bg-bg-raised";
  const inputClass = "border-transparent bg-fill-tertiary text-label-primary placeholder:text-label-tertiary focus:ring-2 focus:ring-focus-ring";
  const softBtn = "border-transparent bg-fill-secondary text-label-primary";

  const loadCourses = useCallback(async () => {
    if (!mintSpaceId) return;
    try {
      setLoading(true);
      const res = await listCourses(mintSpaceId, appInstanceId);
      if (res.success) setCourses(res.data || []);
    } catch (err) {
      setToast?.(err.message || t("courses.failedToLoad"));
    } finally {
      setLoading(false);
    }
  }, [mintSpaceId, appInstanceId, setToast, t]);

  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  const selectedLessonIdRef = useRef(selectedLessonId);
  selectedLessonIdRef.current = selectedLessonId;

  const loadCourseDetail = useCallback(
    async (courseId, { keepLessonId } = {}) => {
      if (!mintSpaceId || !courseId) return;
      try {
        setLoadingCourse(true);
        const res = await getCourseDetail(mintSpaceId, courseId);
        if (res.success) {
          setCourseDetail(res.data);
          setSettingsForm({
            title: res.data.course.title || "",
            description: res.data.course.description || "",
            requireCompletingLessonsInOrder: !!res.data.course.requireCompletingLessonsInOrder,
            visibility: res.data.course.visibility === "private" ? "private" : "public",
          });
          const chapters = res.data.chapters || [];
          const firstLesson =
            chapters.flatMap((ch) => ch.lessons || []).find((l) => l.unlocked || isAdmin) ||
            chapters[0]?.lessons?.[0];
          const nextLessonId = keepLessonId || selectedLessonIdRef.current;
          const stillExists = chapters
            .flatMap((ch) => ch.lessons || [])
            .some((l) => String(l._id) === String(nextLessonId));
          if (stillExists) {
            setSelectedLessonId(nextLessonId);
          } else if (firstLesson) {
            setSelectedLessonId(firstLesson._id);
          }
        }
      } catch (err) {
        setToast?.(err.message || t("courses.failedToLoad"));
      } finally {
        setLoadingCourse(false);
      }
    },
    [mintSpaceId, setToast, t, isAdmin]
  );

  const openCourse = async (courseId) => {
    setActiveCourseId(courseId);
    setView("workspace");
    setSelectedLessonId(null);
    setLessonDetail(null);
    await loadCourseDetail(courseId);
  };

  const backToList = () => {
    setView("list");
    setActiveCourseId(null);
    setCourseDetail(null);
    setSelectedLessonId(null);
    setLessonDetail(null);
    setShowSettings(false);
    loadCourses();
  };

  const loadLesson = useCallback(
    async (lessonId) => {
      if (!mintSpaceId || !activeCourseId || !lessonId) return;
      try {
        setLoadingLesson(true);
        setLessonDetail(null);
        setVideoSrc(null);
        if (videoObjectUrlRef.current) {
          URL.revokeObjectURL(videoObjectUrlRef.current);
          videoObjectUrlRef.current = null;
        }
        const res = await getLessonDetail(mintSpaceId, activeCourseId, lessonId);
        if (res.success) {
          setLessonDetail(res.data);
          setLessonForm({
            title: res.data.lesson.title || "",
            content: res.data.lesson.content || "",
            daysFromCourseStartUntilUnlock:
              res.data.lesson.daysFromCourseStartUntilUnlock || 0,
          });
        }
      } catch (err) {
        setToast?.(err.message || t("courses.lessonLocked"));
        setLessonDetail(null);
      } finally {
        setLoadingLesson(false);
      }
    },
    [mintSpaceId, activeCourseId, setToast, t]
  );

  useEffect(() => {
    if (view === "workspace" && selectedLessonId) {
      loadLesson(selectedLessonId);
    }
  }, [view, selectedLessonId, loadLesson]);

  // Video load
  useEffect(() => {
    let cancelled = false;
    const lesson = lessonDetail?.lesson;
    if (!lesson?.hasVideo || !mintSpaceId || !activeCourseId) return undefined;

    (async () => {
      setVideoLoading(true);
      try {
        const size = lesson.videoSize || 0;
        if (size > 0 && size <= LARGE_VIDEO_BYTES) {
          const blob = await fetchLessonVideoBlob(
            mintSpaceId,
            activeCourseId,
            lesson._id
          );
          if (cancelled) return;
          const url = URL.createObjectURL(blob);
          videoObjectUrlRef.current = url;
          setVideoSrc(url);
        } else {
          setVideoSrc(
            getLessonVideoStreamUrl(mintSpaceId, activeCourseId, lesson._id)
          );
        }
      } catch {
        if (!cancelled) {
          setVideoSrc(
            getLessonVideoStreamUrl(mintSpaceId, activeCourseId, lesson._id)
          );
        }
      } finally {
        if (!cancelled) setVideoLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (videoObjectUrlRef.current) {
        URL.revokeObjectURL(videoObjectUrlRef.current);
        videoObjectUrlRef.current = null;
      }
    };
  }, [lessonDetail?.lesson?._id, lessonDetail?.lesson?.hasVideo, mintSpaceId, activeCourseId]);

  const handleCreateCourse = async () => {
    if (!newCourseTitle.trim()) return;
    setCreatingCourse(true);
    try {
      const res = await createCourse(mintSpaceId, {
        title: newCourseTitle.trim(),
        description: newCourseDesc.trim(),
        instanceId: appInstanceId,
      });
      if (res.success) {
        setToast?.(t("courses.courseCreated"));
        setShowCreateCourse(false);
        setNewCourseTitle("");
        setNewCourseDesc("");
        await openCourse(res.data.course._id);
      }
    } catch (err) {
      setToast?.(err.message || t("courses.failedToSave"));
    } finally {
      setCreatingCourse(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!activeCourseId) return;
    setSavingSettings(true);
    try {
      const res = await updateCourse(mintSpaceId, activeCourseId, settingsForm);
      if (res.success) {
        setToast?.(t("courses.settingsSaved"));
        await loadCourseDetail(activeCourseId, { keepLessonId: selectedLessonId });
        setShowSettings(false);
      }
    } catch (err) {
      setToast?.(err.message || t("courses.failedToSave"));
    } finally {
      setSavingSettings(false);
    }
  };

  const handleCoverUpload = async (file) => {
    if (!file || !activeCourseId) return;
    try {
      await uploadCourseCover(mintSpaceId, activeCourseId, file);
      setToast?.(t("courses.coverUpdated"));
      await loadCourseDetail(activeCourseId, { keepLessonId: selectedLessonId });
    } catch (err) {
      setToast?.(err.message || t("courses.failedToSave"));
    }
  };

  const handleAddChapter = async () => {
    try {
      const res = await createChapter(mintSpaceId, activeCourseId, {});
      if (res.success) {
        setToast?.(t("courses.chapterCreated"));
        await loadCourseDetail(activeCourseId, { keepLessonId: selectedLessonId });
      }
    } catch (err) {
      setToast?.(err.message || t("courses.failedToSave"));
    }
  };

  const handleRenameChapter = async (chapterId, title) => {
    try {
      await updateChapter(mintSpaceId, activeCourseId, chapterId, { title });
      await loadCourseDetail(activeCourseId, { keepLessonId: selectedLessonId });
    } catch (err) {
      setToast?.(err.message || t("courses.failedToSave"));
    }
  };

  const handleAddLesson = async (chapterId, lessonType) => {
    try {
      const res = await createLesson(mintSpaceId, activeCourseId, chapterId, {
        lessonType,
      });
      if (res.success) {
        setToast?.(t("courses.lessonCreated"));
        setAddLessonChapterId(null);
        await loadCourseDetail(activeCourseId, { keepLessonId: res.data._id });
        setSelectedLessonId(res.data._id);
      }
    } catch (err) {
      setToast?.(err.message || t("courses.failedToSave"));
    }
  };

  const handleSaveLesson = async () => {
    if (!selectedLessonId) return;
    setSavingLesson(true);
    try {
      await updateLesson(mintSpaceId, activeCourseId, selectedLessonId, {
        title: lessonForm.title,
        content: lessonForm.content,
        daysFromCourseStartUntilUnlock: Number(lessonForm.daysFromCourseStartUntilUnlock) || 0,
      });
      setToast?.(t("courses.lessonSaved"));
      await loadCourseDetail(activeCourseId, { keepLessonId: selectedLessonId });
      await loadLesson(selectedLessonId);
    } catch (err) {
      setToast?.(err.message || t("courses.failedToSave"));
    } finally {
      setSavingLesson(false);
    }
  };

  const readVideoDuration = (file) =>
    new Promise((resolve) => {
      const el = document.createElement("video");
      el.preload = "metadata";
      el.onloadedmetadata = () => {
        resolve(el.duration || 0);
        URL.revokeObjectURL(el.src);
      };
      el.onerror = () => resolve(0);
      el.src = URL.createObjectURL(file);
    });

  const handleVideoUpload = async (file) => {
    if (!file || !selectedLessonId) return;
    setUploadingMedia(true);
    try {
      const videoDuration = await readVideoDuration(file);
      await uploadLessonVideo(mintSpaceId, activeCourseId, selectedLessonId, {
        video: file,
        videoDuration,
      });
      setToast?.(t("courses.videoUploaded"));
      await loadLesson(selectedLessonId);
      await loadCourseDetail(activeCourseId, { keepLessonId: selectedLessonId });
    } catch (err) {
      setToast?.(err.message || t("courses.failedToSave"));
    } finally {
      setUploadingMedia(false);
    }
  };

  const handlePdfUpload = async (file) => {
    if (!file || !selectedLessonId) return;
    setUploadingMedia(true);
    try {
      await uploadLessonPdf(mintSpaceId, activeCourseId, selectedLessonId, file);
      setToast?.(t("courses.pdfUploaded"));
      await loadLesson(selectedLessonId);
      await loadCourseDetail(activeCourseId, { keepLessonId: selectedLessonId });
    } catch (err) {
      setToast?.(err.message || t("courses.failedToSave"));
    } finally {
      setUploadingMedia(false);
    }
  };

  const handleAttachmentUpload = async (file) => {
    if (!file || !selectedLessonId) return;
    try {
      await uploadLessonAttachment(mintSpaceId, activeCourseId, selectedLessonId, file);
      setToast?.(t("courses.attachmentUploaded"));
      await loadLesson(selectedLessonId);
    } catch (err) {
      setToast?.(err.message || t("courses.failedToSave"));
    }
  };

  const persistProgress = async (watchPosition, duration, completed = false) => {
    if (!selectedLessonId || !activeCourseId) return;
    try {
      await saveLessonProgress(mintSpaceId, activeCourseId, selectedLessonId, {
        watchPosition,
        duration,
        completed,
      });
      if (completed) {
        await loadCourseDetail(activeCourseId, { keepLessonId: selectedLessonId });
      }
    } catch {
      /* ignore debounce errors */
    }
  };

  const handleTimeUpdate = (currentTime, duration) => {
    lastProgressRef.current = currentTime;
    if (progressSaveRef.current) clearTimeout(progressSaveRef.current);
    progressSaveRef.current = setTimeout(() => {
      persistProgress(currentTime, duration, false);
    }, 4000);
  };

  const handleVideoEnded = () => {
    const pos = lastProgressRef.current || 0;
    persistProgress(pos, pos, true);
  };

  const markLessonComplete = async () => {
    await persistProgress(
      lessonDetail?.lesson?.watchPosition || 0,
      lessonDetail?.lesson?.videoDuration || 0,
      true
    );
    setToast?.(t("courses.lessonCompleted"));
    await loadCourseDetail(activeCourseId, { keepLessonId: selectedLessonId });
    await loadLesson(selectedLessonId);
  };

  const moveLesson = async (chapterId, lessonId, direction) => {
    const chapters = courseDetail?.chapters || [];
    const ch = chapters.find((c) => c._id === chapterId);
    if (!ch) return;
    const lessons = [...(ch.lessons || [])];
    const idx = lessons.findIndex((l) => l._id === lessonId);
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (idx < 0 || swapIdx < 0 || swapIdx >= lessons.length) return;
    [lessons[idx], lessons[swapIdx]] = [lessons[swapIdx], lessons[idx]];
    const payload = chapters.map((c) => ({
      _id: c._id,
      order: c.order,
      lessons: (c._id === chapterId ? lessons : c.lessons || []).map((l, i) => ({
        _id: l._id,
        order: i,
        chapterId: c._id,
      })),
    }));
    try {
      await reorderCourse(mintSpaceId, activeCourseId, payload);
      await loadCourseDetail(activeCourseId, { keepLessonId: selectedLessonId });
    } catch (err) {
      setToast?.(err.message || t("courses.failedToSave"));
    }
  };

  const moveChapter = async (chapterId, direction) => {
    const chapters = [...(courseDetail?.chapters || [])];
    const idx = chapters.findIndex((c) => c._id === chapterId);
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (idx < 0 || swapIdx < 0 || swapIdx >= chapters.length) return;
    [chapters[idx], chapters[swapIdx]] = [chapters[swapIdx], chapters[idx]];
    const payload = chapters.map((c, i) => ({
      _id: c._id,
      order: i,
      lessons: (c.lessons || []).map((l, li) => ({
        _id: l._id,
        order: li,
        chapterId: c._id,
      })),
    }));
    try {
      await reorderCourse(mintSpaceId, activeCourseId, payload);
      await loadCourseDetail(activeCourseId, { keepLessonId: selectedLessonId });
    } catch (err) {
      setToast?.(err.message || t("courses.failedToSave"));
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.type === "course") {
        await deleteCourse(mintSpaceId, deleteTarget.id);
        setToast?.(t("courses.courseDeleted"));
        setDeleteTarget(null);
        backToList();
        return;
      }
      if (deleteTarget.type === "chapter") {
        await deleteChapter(mintSpaceId, activeCourseId, deleteTarget.id);
        setToast?.(t("courses.chapterDeleted"));
      } else if (deleteTarget.type === "lesson") {
        await deleteLesson(mintSpaceId, activeCourseId, deleteTarget.id);
        setToast?.(t("courses.lessonDeleted"));
        if (selectedLessonId === deleteTarget.id) {
          setSelectedLessonId(null);
          setLessonDetail(null);
        }
      } else if (deleteTarget.type === "attachment") {
        await deleteLessonAttachment(mintSpaceId, activeCourseId, deleteTarget.id);
        setToast?.(t("courses.attachmentDeleted"));
        await loadLesson(selectedLessonId);
        setDeleteTarget(null);
        return;
      }
      setDeleteTarget(null);
      await loadCourseDetail(activeCourseId, { keepLessonId: selectedLessonId });
    } catch (err) {
      setToast?.(err.message || t("courses.failedToDelete"));
      setDeleteTarget(null);
    }
  };

  const lockHint = (lesson) => {
    if (!lesson?.lockReason) return t("courses.lessonLocked");
    if (lesson.lockReason.type === "drip") {
      const days = lesson.lockReason.daysRemaining || 1;
      return (t("courses.unlocksInDays") || "Unlocks in {days} day(s)").replace(
        "{days}",
        String(days)
      );
    }
    if (lesson.lockReason.type === "sequential") {
      return t("courses.completePrevious");
    }
    return t("courses.lessonLocked");
  };

  const listMenuItems = [
    isAdmin &&
      onOpenProperties && {
        id: "properties",
        label: t("mintSpace.appProperties") || "Properties",
        icon: FiSettings,
        onClick: onOpenProperties,
      },
    isAdmin &&
      onCreateProduct && {
        id: "sell-access",
        label: t("courses.sellAccess"),
        icon: FiPlus,
        onClick: onCreateProduct,
      },
  ].filter(Boolean);

  const coursesDesktopBg = "";

  if (loading) {
    return (
      <MintSpaceAppWindow
        isDark={isDark}
        app="courses"
        icon={FiBookOpen}
        iconGradient="from-blue-500 to-cyan-500"
        title={t("courses.title")}
        subtitle={t("courses.userSubtitle")}
        menuItems={listMenuItems}
        bodyClassName={coursesDesktopBg}
      >
        <div className={`flex h-full items-center justify-center ${mutedText}`}>
          <FiLoader className="h-6 w-6 animate-spin text-accent-text" />
          <span className="ml-2 text-subheadline">{t("courses.loading")}</span>
        </div>
      </MintSpaceAppWindow>
    );
  }

  /* ---------- COURSE LIST ---------- */
  if (view === "list") {
    return (
      <MintSpaceAppWindow
        isDark={isDark}
        app="courses"
        icon={FiBookOpen}
        iconGradient="from-blue-500 to-cyan-500"
        title={t("courses.title")}
        subtitle={t("courses.userSubtitle")}
        menuItems={listMenuItems}
        actions={
          isAdmin ? (
            <Button
              size="m"
              icon={<Icon name="plus" size={20} />}
              className="!bg-app-courses-base !text-app-courses-on"
              onClick={() => setShowCreateCourse(true)}
            >
              {t("courses.addCourse")}
            </Button>
          ) : null
        }
        bodyClassName={coursesDesktopBg}
      >
        <div>
          {showCreateCourse && (
            <div className="mint-space-card-enter mb-space-24 flex flex-col gap-space-8 rounded-radius-lg bg-bg-raised p-space-16">
              <h3 className={`mb-2 text-caption-1-semibold ${baseText}`}>
                {t("courses.createCourse")}
              </h3>
              <input
                value={newCourseTitle}
                onChange={(e) => setNewCourseTitle(e.target.value)}
                placeholder={t("courses.courseTitle")}
                className={`mb-1.5 w-full rounded-radius-xs border px-2.5 py-1.5 text-footnote outline-none ${inputClass}`}
              />
              <textarea
                value={newCourseDesc}
                onChange={(e) => setNewCourseDesc(e.target.value)}
                placeholder={t("courses.courseDescription")}
                rows={2}
                className={`mb-2 w-full rounded-radius-xs border px-2.5 py-1.5 text-footnote outline-none ${inputClass}`}
              />
              <div className="flex gap-1.5">
                <button
                  type="button"
                  disabled={creatingCourse || !newCourseTitle.trim()}
                  onClick={handleCreateCourse}
                  className="rounded-radius-xs bg-accent-default px-2.5 py-1.5 text-caption-1-semibold text-accent-on disabled:opacity-50"
                >
                  {creatingCourse ? t("courses.creating") : t("courses.create")}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateCourse(false)}
                  className={`rounded-radius-xs border px-2.5 py-1.5 text-caption-1 ${softBtn}`}
                >
                  {t("courses.cancel")}
                </button>
              </div>
            </div>
          )}

          {courses.length === 0 ? (
            <EmptyState icon="book" title={t("courses.emptyTitle")} message={t("courses.emptyHint")} />
          ) : (
            <div className="grid grid-cols-1 gap-space-16 min-[600px]:grid-cols-2 lg:gap-space-24 xl:grid-cols-3">
              {courses.map((course, idx) => {
                const total = course.lessonCount || 0;
                const done = course.completedLessonCount || 0;
                const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                return (
                  <button
                    key={course._id}
                    type="button"
                    onClick={() => openCourse(course._id)}
                    style={{ animationDelay: `${Math.min(idx, 8) * 40}ms` }}
                    className="mint-space-card-enter flex flex-col overflow-hidden rounded-radius-lg bg-bg-raised text-left outline-none focus-visible:shadow-[0_0_0_2px_var(--color-focus-ring)]"
                  >
                    <div className="relative h-[180px] w-full shrink-0 overflow-hidden bg-app-courses-deep">
                      {course.coverImage ? (
                        <img
                          src={
                            /^https?:\/\//i.test(course.coverImage)
                              ? course.coverImage
                              : getCourseCoverUrl(mintSpaceId, course._id)
                          }
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <AppCover app="courses" size="phone" />
                      )}
                      {course.visibility === "private" && (
                        <Badge size="s" className="absolute right-space-12 top-space-12">
                          {t("courses.visibilityPrivate")}
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-col items-start gap-space-8 p-space-16">
                      <Badge size="s">
                        {(t("courses.lessonProgress") || "{done}/{total} lessons")
                          .replace("{done}", String(done))
                          .replace("{total}", String(total))}
                      </Badge>
                      <h3 className="w-full truncate text-headline text-label-primary">{course.title}</h3>
                      {course.description ? (
                        <p className="line-clamp-2 text-footnote text-label-secondary">{course.description}</p>
                      ) : null}
                      {total > 0 && <ProgressBar value={pct / 100} className="mt-space-4" />}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <ConfirmModal
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
          title={t("courses.confirmDeleteTitle")}
          message={t("courses.confirmDelete")}
          isDark={isDark}
        />
      </MintSpaceAppWindow>
    );
  }

  /* ---------- WORKSPACE ---------- */
  const chapters = courseDetail?.chapters || [];
  const course = courseDetail?.course;

  const workspaceMenuItems = [
    isAdmin &&
      onOpenProperties && {
        id: "properties",
        label: t("mintSpace.appProperties") || "Properties",
        icon: FiSettings,
        onClick: onOpenProperties,
      },
    isAdmin &&
      onCreateProduct && {
        id: "sell-access",
        label: t("courses.sellAccess"),
        icon: FiPlus,
        onClick: onCreateProduct,
      },
    isAdmin && {
      id: "settings",
      label: t("courses.settings"),
      icon: FiSettings,
      onClick: () => setShowSettings((s) => !s),
    },
    isAdmin && { type: "separator", id: "sep-delete" },
    isAdmin && {
      id: "delete-course",
      label: t("common.delete"),
      icon: FiTrash2,
      danger: true,
      onClick: () =>
        setDeleteTarget({ type: "course", id: activeCourseId, label: course?.title }),
    },
  ].filter(Boolean);

  return (
    <MintSpaceAppWindow
      isDark={isDark}
      icon={FiBookOpen}
      iconGradient="from-blue-500 to-cyan-500"
      title={t("courses.title")}
      subtitle={course?.title || (isAdmin ? t("courses.adminSubtitle") : t("courses.userSubtitle"))}
      menuItems={workspaceMenuItems}
      bodyOverflow="hidden"
      bodyClassName="flex h-full min-h-0 flex-col"
      toolbar={
        <>
          <div
            className="mb-space-16 flex flex-wrap items-center gap-space-8"
          >
            <button
              type="button"
              onClick={backToList}
              className={`inline-flex items-center gap-1 rounded-radius-xs px-2 py-1.5 text-footnote font-medium transition ${mutedText} hover:text-accent-text`}
            >
              <FiChevronLeft className="h-4 w-4" />
              {t("courses.backToList")}
            </button>
            {courseDetail?.progress && (
              <span className={`ml-auto text-footnote ${mutedText}`}>
                {(t("courses.lessonProgress") || "{done}/{total} lessons")
                  .replace("{done}", String(courseDetail.progress.completedLessonCount || 0))
                  .replace("{total}", String(courseDetail.progress.totalLessonCount || 0))}
              </span>
            )}
          </div>

          {showSettings && isAdmin && (
            <div
              className={`border-b px-4 py-4 ${
                "mb-space-16 rounded-radius-lg border-transparent bg-bg-raised"
              }`}
            >
              <div className="mx-auto max-w-2xl space-y-3">
                <input
                  value={settingsForm.title}
                  onChange={(e) => setSettingsForm((f) => ({ ...f, title: e.target.value }))}
                  className={`w-full rounded-radius-sm border px-3 py-2 text-subheadline outline-none ${inputClass}`}
                  placeholder={t("courses.courseTitle")}
                />
                <textarea
                  value={settingsForm.description}
                  onChange={(e) => setSettingsForm((f) => ({ ...f, description: e.target.value }))}
                  rows={3}
                  className={`w-full rounded-radius-sm border px-3 py-2 text-subheadline outline-none ${inputClass}`}
                  placeholder={t("courses.courseDescription")}
                />
                <label className={`flex items-center gap-2 text-subheadline ${baseText}`}>
                  <input
                    type="checkbox"
                    checked={settingsForm.requireCompletingLessonsInOrder}
                    onChange={(e) =>
                      setSettingsForm((f) => ({
                        ...f,
                        requireCompletingLessonsInOrder: e.target.checked,
                      }))
                    }
                  />
                  {t("courses.requireCompletion")}
                </label>
                <p className={`text-footnote ${mutedText}`}>{t("courses.requireCompletionHint")}</p>

                {/* Who can see this course — plus a shortcut to sell access as a product */}
                <div className="space-y-2 pt-1">
                  <div>
                    <p className={`text-footnote-semibold ${baseText}`}>
                      {t("courses.visibility")}
                    </p>
                    <p className={`text-caption-1 ${mutedText}`}>{t("courses.visibilityHint")}</p>
                  </div>
                  {[
                    {
                      id: "public",
                      icon: FiGlobe,
                      label: t("courses.visibilityPublic"),
                      hint: t("courses.visibilityPublicHint"),
                    },
                    {
                      id: "private",
                      icon: FiLock,
                      label: t("courses.visibilityPrivate"),
                      hint: t("courses.visibilityPrivateHint"),
                    },
                  ].map((opt) => {
                    const Icon = opt.icon;
                    const active = settingsForm.visibility === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setSettingsForm((f) => ({ ...f, visibility: opt.id }))}
                        className={`flex w-full items-center justify-between gap-2 rounded-radius-sm border px-3 py-2.5 text-left transition ${
                          active ? "border-app-courses-base bg-app-courses-deep" : softBtn
                        }`}
                      >
                        <span className="flex min-w-0 items-center gap-2.5">
                          <Icon
                            className={`h-4 w-4 shrink-0 ${active ? "text-app-courses-soft" : mutedText}`}
                          />
                          <span className="min-w-0">
                            <span className={`block text-footnote-semibold ${baseText}`}>
                              {opt.label}
                            </span>
                            <span className={`block text-caption-1 ${mutedText}`}>{opt.hint}</span>
                          </span>
                        </span>
                        <span
                          className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 ${
                            active
                              ? "border-accent-default"
                              : "border-separator-opaque"
                          }`}
                        >
                          {active && <span className="h-2 w-2 rounded-full bg-accent-default" />}
                        </span>
                      </button>
                    );
                  })}
                  {onCreateProduct && (
                    <button
                      type="button"
                      onClick={onCreateProduct}
                      className={`flex w-full items-center justify-between gap-2 rounded-radius-sm border px-3 py-2.5 text-left transition ${softBtn}`}
                    >
                      <span className="flex min-w-0 items-center gap-2.5">
                        <FiDollarSign className={`h-4 w-4 shrink-0 ${mutedText}`} />
                        <span className="min-w-0">
                          <span className={`block text-footnote-semibold ${baseText}`}>
                            {t("courses.sellAsProduct")}
                          </span>
                          <span className={`block text-caption-1 ${mutedText}`}>
                            {t("courses.sellAsProductHint")}
                          </span>
                        </span>
                      </span>
                      <FiChevronRight className={`h-4 w-4 shrink-0 ${mutedText}`} />
                    </button>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => coverInputRef.current?.click()}
                    className={`inline-flex items-center gap-1.5 rounded-radius-sm border px-3 py-2 text-footnote-semibold ${
                      "border-separator-default"
                    } ${baseText}`}
                  >
                    <FiImage className="h-3.5 w-3.5" />
                    {t("courses.uploadCover")}
                  </button>
                  <input
                    ref={coverInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleCoverUpload(f);
                      e.target.value = "";
                    }}
                  />
                  <button
                    type="button"
                    disabled={savingSettings}
                    onClick={handleSaveSettings}
                    className="rounded-radius-sm bg-accent-default px-4 py-2 text-footnote-semibold text-accent-on disabled:opacity-50"
                  >
                    {savingSettings ? t("courses.saving") : t("courses.saveSettings")}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      }
    >
      <div className="flex min-h-0 flex-1 flex-col gap-space-24 lg:flex-row">
        {/* Left: chapters / lessons */}
        <aside
          className="campaign-window-scrollbar w-full shrink-0 overflow-y-auto rounded-radius-lg bg-bg-raised p-space-16 lg:w-[280px]"
        >
          {loadingCourse ? (
            <div className="flex items-center gap-2 p-2.5 text-caption-1">
              <FiLoader className="h-3.5 w-3.5 animate-spin text-accent-text" />
              <span className={mutedText}>{t("courses.loading")}</span>
            </div>
          ) : (
            <div className="p-1.5">
              {chapters.map((chapter, chIdx) => (
                <div key={chapter._id} className="mb-2.5">
                  <div className="mb-0.5 flex items-center gap-1">
                    {isAdmin ? (
                      <input
                        defaultValue={chapter.title}
                        key={`${chapter._id}-${chapter.title}`}
                        onBlur={(e) => {
                          const v = e.target.value.trim();
                          if (v && v !== chapter.title) handleRenameChapter(chapter._id, v);
                        }}
                        className={`min-w-0 flex-1 rounded-radius-xs border-0 bg-transparent px-1 py-1 text-footnote-semibold uppercase tracking-wide outline-none ${baseText}`}
                      />
                    ) : (
                      <p className={`flex-1 px-1 text-footnote-semibold uppercase tracking-wide ${mutedText}`}>
                        {chapter.title}
                      </p>
                    )}
                    {isAdmin && (
                      <div className="flex shrink-0 items-center gap-0.5">
                        <button
                          type="button"
                          disabled={chIdx === 0}
                          onClick={() => moveChapter(chapter._id, "up")}
                          className={`rounded p-1 ${mutedText} disabled:opacity-30`}
                        >
                          <FiChevronUp className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          disabled={chIdx === chapters.length - 1}
                          onClick={() => moveChapter(chapter._id, "down")}
                          className={`rounded p-1 ${mutedText} disabled:opacity-30`}
                        >
                          <FiChevronDown className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setDeleteTarget({
                              type: "chapter",
                              id: chapter._id,
                              label: chapter.title,
                            })
                          }
                          className="rounded p-1 text-status-danger"
                        >
                          <FiTrash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                  </div>

                  <ul className="space-y-0.5">
                    {(chapter.lessons || []).map((lesson, lIdx) => {
                      const locked = !lesson.unlocked && !isAdmin;
                      const active = selectedLessonId === lesson._id;
                      return (
                        <li key={lesson._id} className="flex items-center gap-0.5">
                          <button
                            type="button"
                            disabled={locked}
                            onClick={() => !locked && setSelectedLessonId(lesson._id)}
                            title={locked ? lockHint(lesson) : lesson.title}
                            className={`flex min-w-0 flex-1 items-center gap-1.5 rounded-md px-1.5 py-1.5 text-left text-caption-1 transition ${
                              active
                                ? "bg-app-courses-deep text-app-courses-soft"
                                : locked
                                  ? "cursor-not-allowed opacity-50"
                                  : "hover:bg-fill-quaternary"
                            } ${baseText}`}
                          >
                            {lesson.completed ? (
                              <FiCheckCircle className="h-3.5 w-3.5 shrink-0 text-accent-text" />
                            ) : locked ? (
                              <FiLock className="h-3.5 w-3.5 shrink-0" />
                            ) : lesson.lessonType === "pdf" ? (
                              <FiFileText className="h-3.5 w-3.5 shrink-0" />
                            ) : (
                              <FiVideo className="h-3.5 w-3.5 shrink-0" />
                            )}
                            <span className="min-w-0 flex-1 truncate">{lesson.title}</span>
                            {lesson.videoDuration > 0 && (
                              <span className={`shrink-0 text-caption-1 ${mutedText}`}>
                                {formatDuration(lesson.videoDuration)}
                              </span>
                            )}
                          </button>
                          {isAdmin && (
                            <div className="flex shrink-0">
                              <button
                                type="button"
                                disabled={lIdx === 0}
                                onClick={() => moveLesson(chapter._id, lesson._id, "up")}
                                className={`rounded p-1 ${mutedText} disabled:opacity-30`}
                              >
                                <FiChevronUp className="h-3 w-3" />
                              </button>
                              <button
                                type="button"
                                disabled={lIdx === (chapter.lessons || []).length - 1}
                                onClick={() => moveLesson(chapter._id, lesson._id, "down")}
                                className={`rounded p-1 ${mutedText} disabled:opacity-30`}
                              >
                                <FiChevronDown className="h-3 w-3" />
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  setDeleteTarget({
                                    type: "lesson",
                                    id: lesson._id,
                                    label: lesson.title,
                                  })
                                }
                                className="rounded p-1 text-status-danger"
                              >
                                <FiTrash2 className="h-3 w-3" />
                              </button>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>

                  {isAdmin && (
                    <div className="mt-1.5">
                      <Button
                        size="s"
                        variant="plain"
                        icon={<Icon name="plus" size={16} />}
                        aria-haspopup="menu"
                        aria-expanded={addLessonChapterId === chapter._id}
                        onClick={(e) => {
                          lessonMenuAnchor.current = e.currentTarget;
                          setAddLessonChapterId(addLessonChapterId === chapter._id ? null : chapter._id);
                        }}
                      >
                        {t("courses.addLesson")}
                      </Button>
                    </div>
                  )}
                </div>
              ))}

              <Menu
                open={!!addLessonChapterId}
                onClose={closeLessonMenu}
                anchorRef={lessonMenuAnchor}
                width={220}
                align="start"
                label={t("courses.addLesson")}
              >
                <MenuItem icon="video" onClick={() => handleAddLesson(addLessonChapterId, "multimedia")}>
                  {t("courses.multimedia")}
                </MenuItem>
                <MenuItem icon="file" onClick={() => handleAddLesson(addLessonChapterId, "pdf")}>
                  {t("courses.pdfLesson")}
                </MenuItem>
              </Menu>

              {isAdmin && (
                <button
                  type="button"
                  onClick={handleAddChapter}
                  className="mt-space-4 h-button-m w-full rounded-radius-md bg-fill-secondary px-space-16 text-caps-button-m uppercase text-label-primary"
                >
                  <FiPlus className="mr-1 inline h-3.5 w-3.5" />
                  {t("courses.addChapter")}
                </button>
              )}
            </div>
          )}
        </aside>

        {/* Right: lesson content */}
        <main className="min-w-0 flex-1 overflow-y-auto rounded-radius-lg bg-bg-raised p-space-16 lg:p-space-24">
          {!selectedLessonId ? (
            <div className={`flex h-48 flex-col items-center justify-center ${mutedText}`}>
              <FiBookOpen className="mb-2 h-8 w-8" />
              <p className="text-subheadline">{t("courses.selectLesson")}</p>
            </div>
          ) : loadingLesson ? (
            <div className="flex h-48 items-center justify-center gap-2">
              <FiLoader className="h-5 w-5 animate-spin text-accent-text" />
              <span className={`text-subheadline ${mutedText}`}>{t("courses.loading")}</span>
            </div>
          ) : !lessonDetail ? (
            <div className={`rounded-radius-sm border p-4 text-center ${cardBase}`}>
              <FiLock className={`mx-auto mb-2 h-6 w-6 ${mutedText}`} />
              <p className={`text-subheadline ${baseText}`}>{t("courses.lessonLocked")}</p>
            </div>
          ) : (
            <div className="mx-auto max-w-3xl space-y-5">
              {isAdmin ? (
                <input
                  value={lessonForm.title}
                  onChange={(e) => setLessonForm((f) => ({ ...f, title: e.target.value }))}
                  className={`w-full border-0 bg-transparent text-title-3 outline-none ${baseText}`}
                  placeholder={t("courses.lessonTitle")}
                />
              ) : (
                <h1 className={`text-title-3 ${baseText}`}>
                  {lessonDetail.lesson.title}
                </h1>
              )}

              {isAdmin && (
                <div className="flex flex-wrap items-end gap-3">
                  <label className={`block text-footnote ${mutedText}`}>
                    {t("courses.dripLabel")}
                    <input
                      type="number"
                      min={0}
                      value={lessonForm.daysFromCourseStartUntilUnlock}
                      onChange={(e) =>
                        setLessonForm((f) => ({
                          ...f,
                          daysFromCourseStartUntilUnlock: e.target.value,
                        }))
                      }
                      className={`mt-1 w-28 rounded-radius-sm border px-3 py-2 text-subheadline outline-none ${inputClass}`}
                    />
                  </label>
                  <p className={`pb-2 text-footnote ${mutedText}`}>{t("courses.dripHint")}</p>
                </div>
              )}

              {/* Multimedia video */}
              {(lessonDetail.lesson.lessonType === "multimedia" ||
                lessonDetail.lesson.hasVideo) && (
                <div>
                  {videoLoading ? (
                    <div
                      className={`flex aspect-video items-center justify-center rounded-radius-md ${
                        "bg-fill-tertiary"
                      }`}
                    >
                      <FiLoader className="h-6 w-6 animate-spin text-accent-text" />
                      <span className={`ml-2 text-subheadline ${mutedText}`}>{t("courses.loadingVideo")}</span>
                    </div>
                  ) : videoSrc ? (
                    <CustomVideoPlayer
                      src={videoSrc}
                      isDark={isDark}
                      initialTime={lessonDetail.lesson.watchPosition || 0}
                      onTimeUpdate={handleTimeUpdate}
                      onEnded={handleVideoEnded}
                    />
                  ) : (
                    <div
                      className={`flex aspect-video flex-col items-center justify-center rounded-radius-md border border-dashed ${
                        "border-separator-opaque"
                      }`}
                    >
                      <FiVideo className={`mb-2 h-8 w-8 ${mutedText}`} />
                      <p className={`text-subheadline ${mutedText}`}>{t("courses.noVideoYet")}</p>
                    </div>
                  )}
                  {isAdmin && (
                    <div className="mt-2">
                      <button
                        type="button"
                        disabled={uploadingMedia}
                        onClick={() => videoInputRef.current?.click()}
                        className="inline-flex items-center gap-2 rounded-radius-sm bg-accent-default px-3 py-2 text-footnote-semibold text-accent-on disabled:opacity-50"
                      >
                        {uploadingMedia ? (
                          <FiLoader className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <FiUpload className="h-3.5 w-3.5" />
                        )}
                        {t("courses.uploadVideo")}
                      </button>
                      <input
                        ref={videoInputRef}
                        type="file"
                        accept="video/*"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) handleVideoUpload(f);
                          e.target.value = "";
                        }}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* PDF */}
              {(lessonDetail.lesson.lessonType === "pdf" || lessonDetail.lesson.hasPdf) && (
                <div className={`rounded-radius-md border p-4 ${cardBase}`}>
                  {lessonDetail.lesson.hasPdf ? (
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <FiFileText className="h-5 w-5 text-accent-text" />
                        <div>
                          <p className={`text-subheadline-semibold ${baseText}`}>
                            {lessonDetail.lesson.pdfOriginalName || t("courses.pdfLesson")}
                          </p>
                          {lessonDetail.lesson.pdfSize > 0 && (
                            <p className={`text-footnote ${mutedText}`}>
                              {formatFileSize(lessonDetail.lesson.pdfSize)}
                            </p>
                          )}
                        </div>
                      </div>
                      <a
                        href={getLessonPdfUrl(mintSpaceId, activeCourseId, lessonDetail.lesson._id)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-radius-sm bg-accent-default px-3 py-2 text-footnote-semibold text-accent-on"
                      >
                        <FiDownload className="h-3.5 w-3.5" />
                        {t("courses.openPdf")}
                      </a>
                    </div>
                  ) : (
                    <p className={`text-subheadline ${mutedText}`}>{t("courses.noPdfYet")}</p>
                  )}
                  {isAdmin && (
                    <button
                      type="button"
                      disabled={uploadingMedia}
                      onClick={() => pdfInputRef.current?.click()}
                      className="mt-3 inline-flex items-center gap-2 rounded-radius-sm border border-accent-default px-3 py-2 text-footnote-semibold text-accent-text disabled:opacity-50"
                    >
                      <FiUpload className="h-3.5 w-3.5" />
                      {t("courses.uploadPdf")}
                    </button>
                  )}
                  <input
                    ref={pdfInputRef}
                    type="file"
                    accept="application/pdf,.pdf"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handlePdfUpload(f);
                      e.target.value = "";
                    }}
                  />
                </div>
              )}

              {/* Attachments (multimedia) */}
              {lessonDetail.lesson.lessonType !== "pdf" && (
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <h3 className={`text-subheadline-semibold ${baseText}`}>{t("courses.attachments")}</h3>
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={() => attachmentInputRef.current?.click()}
                        className={`inline-flex items-center gap-1 text-footnote-semibold text-accent-text`}
                      >
                        <FiPaperclip className="h-3.5 w-3.5" />
                        {t("courses.addAttachment")}
                      </button>
                    )}
                    <input
                      ref={attachmentInputRef}
                      type="file"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleAttachmentUpload(f);
                        e.target.value = "";
                      }}
                    />
                  </div>
                  {(lessonDetail.attachments || []).length === 0 ? (
                    <p className={`text-footnote ${mutedText}`}>{t("courses.noAttachments")}</p>
                  ) : (
                    <ul className="space-y-1.5">
                      {(lessonDetail.attachments || []).map((att) => (
                        <li
                          key={att._id}
                          className={`flex items-center justify-between rounded-radius-sm border px-3 py-2 ${cardBase}`}
                        >
                          <span className={`truncate text-subheadline ${baseText}`}>{att.name}</span>
                          <div className="flex items-center gap-2">
                            <span className={`text-caption-1 ${mutedText}`}>
                              {formatFileSize(att.size)}
                            </span>
                            <a
                              href={getLessonAttachmentDownloadUrl(
                                mintSpaceId,
                                activeCourseId,
                                att._id
                              )}
                              className="text-accent-text"
                            >
                              <FiDownload className="h-4 w-4" />
                            </a>
                            {isAdmin && (
                              <button
                                type="button"
                                onClick={() =>
                                  setDeleteTarget({
                                    type: "attachment",
                                    id: att._id,
                                    label: att.name,
                                  })
                                }
                                className="text-status-danger"
                              >
                                <FiTrash2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {/* Rich text / content */}
              <div>
                <h3 className={`mb-2 text-subheadline-semibold ${baseText}`}>{t("courses.lessonContent")}</h3>
                {isAdmin ? (
                  <textarea
                    value={lessonForm.content}
                    onChange={(e) => setLessonForm((f) => ({ ...f, content: e.target.value }))}
                    rows={8}
                    placeholder={t("courses.lessonContentPlaceholder")}
                    className={`w-full rounded-radius-sm border px-3 py-2 text-subheadline outline-none ${inputClass}`}
                  />
                ) : lessonDetail.lesson.content ? (
                  <div
                    className={`prose prose-sm max-w-none whitespace-pre-wrap text-subheadline ${
                      "text-label-secondary"
                    }`}
                  >
                    {lessonDetail.lesson.content}
                  </div>
                ) : (
                  <p className={`text-subheadline ${mutedText}`}>{t("courses.noContent")}</p>
                )}
              </div>

              <div className="flex flex-wrap gap-2 pt-2">
                {isAdmin && (
                  <button
                    type="button"
                    disabled={savingLesson}
                    onClick={handleSaveLesson}
                    className="rounded-radius-sm bg-accent-default px-4 py-2 text-subheadline-semibold text-accent-on disabled:opacity-50"
                  >
                    {savingLesson ? t("courses.saving") : t("courses.saveChanges")}
                  </button>
                )}
                {!lessonDetail.lesson.completed && (
                  <button
                    type="button"
                    onClick={markLessonComplete}
                    className={`rounded-radius-sm border px-4 py-2 text-subheadline-semibold ${
                      "border-separator-default"
                    } ${baseText}`}
                  >
                    <FiCheckCircle className="mr-1.5 inline h-4 w-4 text-accent-text" />
                    {t("courses.markComplete")}
                  </button>
                )}
                {lessonDetail.lesson.completed && (
                  <span className="inline-flex items-center gap-1.5 text-subheadline-semibold text-accent-text">
                    <FiCheckCircle className="h-4 w-4" />
                    {t("courses.completed")}
                  </span>
                )}
              </div>
            </div>
          )}
        </main>
      </div>

      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title={t("courses.confirmDeleteTitle")}
        message={t("courses.confirmDelete")}
        isDark={isDark}
      />
    </MintSpaceAppWindow>
  );
};

export default MintSpaceCoursesPage;
